'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import { ImArrowRight } from 'react-icons/im';
import { FaRegCirclePlay } from 'react-icons/fa6';
import VideoModal from '../videoModal';
import { BsFilm } from 'react-icons/bs';
import { GetCaseStudySettingsResult } from '@/sanity.types';
import { urlForImage } from '@/sanity/lib/utils';
import { imageData } from '@/config/defaultBlur';

type WorldChangeProps = {
  title: NonNullable<GetCaseStudySettingsResult>['videoList']['title'];
  featured?: NonNullable<GetCaseStudySettingsResult>['videoList']['featured'];
  videos: NonNullable<GetCaseStudySettingsResult>['videoList']['videos'];
};

const WorldChange = ({ title, featured, videos }: WorldChangeProps) => {
  const [modalShow, setModalShow] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);

  const handleVideoClick = (url: string) => {
    setCurrentVideoUrl(url);
    setModalShow(true);
  };
  return (
    <>
      <div className="changing_world">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-12">
              <h2 className="text-center section-title">{title}</h2>
            </div>
          </div>

          {/* Featured Section */}
          {featured && (
            <div className="row justify-content-center">
              <div className="col-md-10 col-sm-10">
                <div className="row justify-content-center">
                  <div className="col-md-6">
                    <div className="video-thumb">
                      <Link
                        href="#"
                        className="modal-video"
                        role="button"
                        aria-label={`Play video: ${featured.title}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!featured.videoUrl) return;
                          handleVideoClick(featured.videoUrl);
                        }}
                      >
                        <div className="thumb-img">
                          <Image
                            src={
                              (featured?.thumbnail &&
                                (urlForImage(
                                  featured?.thumbnail
                                )?.url() as string)) ||
                              '/n_images/default-blog.jpg'
                            }
                            alt={'video alt'}
                            height={153}
                            width={360}
                            quality={100}
                            placeholder="blur"
                            blurDataURL={
                              featured?.thumbnail
                                ? featured?.thumbnail?.imgAsset?.metadata?.lqip
                                : imageData.blurImage
                            }
                          />
                          <div className="play_icon">
                            <FaRegCirclePlay />
                          </div>
                        </div>
                      </Link>
                    </div>
                    <h2 className="name">
                      <Link
                        href="#"
                        className="modal-video"
                        role="button"
                        aria-label="Watch featured video"
                        onClick={(e) => {
                          e.preventDefault();
                          if (!featured.videoUrl) return;
                          handleVideoClick(featured?.videoUrl);
                        }}
                      >
                        {featured.name}
                      </Link>
                    </h2>
                    <h3 className="name">{featured.title}</h3>
                    <div className="links">
                      <Link
                        href="#"
                        className="modal-video"
                        role="button"
                        aria-label={`Play video: ${featured.title}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!featured.videoUrl) return;
                          handleVideoClick(featured.videoUrl);
                        }}
                      >
                        <span className="video-icon">
                          <BsFilm />
                        </span>{' '}
                        Watch Video
                      </Link>
                      <span className="runtime">{featured.runtime}</span>
                    </div>
                    <p className="desc">{featured.description}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Videos Section */}
          <div className="row justify-content-center">
            {videos.map((video, index) => (
              <div className="col-md-4" key={index}>
                <div className="video-thumb">
                  <Link
                    href="#"
                    className="modal-video"
                    role="button"
                    aria-label={`Play video: ${video.title}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!video.videoUrl) return;
                      handleVideoClick(video.videoUrl);
                    }}
                  >
                    <div className="thumb-img">
                      <Image
                        src={
                          (video?.thumbnail &&
                            (urlForImage(video?.thumbnail)?.url() as string)) ||
                          '/n_images/default-blog.jpg'
                        }
                        alt={'video alt'}
                        height={153}
                        width={360}
                        quality={100}
                        placeholder="blur"
                        blurDataURL={
                          video?.thumbnail
                            ? video?.thumbnail?.imgAsset?.metadata?.lqip
                            : imageData.blurImage
                        }
                      />
                      <div className="play_icon">
                        <FaRegCirclePlay />
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="video-name">
                  <Link
                    href="#"
                    className="modal-video"
                    role="button"
                    aria-label={`Play video: ${video.title}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!video.videoUrl) return;
                      handleVideoClick(video.videoUrl);
                    }}
                  >
                    {video.title}
                  </Link>
                </div>
                <div className="video-time">
                  <Link
                    href="#"
                    className="modal-video"
                    aria-label={`Play video: ${video.title}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!video.videoUrl) return;
                      handleVideoClick(video.videoUrl);
                    }}
                  >
                    <span className="video-icon">
                      <BsFilm />
                    </span>{' '}
                    Watch Video
                  </Link>
                  <div className="runtime">{video.runtime}</div>
                </div>
                <p className="desc">{video.description}</p>
              </div>
            ))}
          </div>

          {/* More Videos */}
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="video-time text-bottom">
                <Link
                  href="/videos"
                  className="modal-video"
                  aria-label="More videos from SignUpGenius"
                >
                  More videos from SignUpGenius
                  <span className="link-icon">
                    <ImArrowRight />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <VideoModal
        videoUrl={currentVideoUrl || ''}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
    </>
  );
};

export default WorldChange;
